/**
 * Geometry-Dash-style camera for one view.
 *
 * Horizontal: the followed racer sits PLAYER_SCREEN_X (30 %) from the left edge.
 *
 * Zoom (pixels per block) follows one rule on every device: show LOOKAHEAD_SECONDS of travel
 * ahead of the racer at the fastest speed coming up (never less than MIN_LOOKAHEAD_BLOCKS),
 * and zoom out further only as much as the vertical needs require — the whole corridor of
 * ship / ball / UFO / wave / spider stretches plus a margin, MIN_FREE_HEIGHT blocks elsewhere.
 * A bigger screen therefore shows the same lookahead, only sharper; nobody sees further ahead
 * than the rule allows unless their view is too flat for the corridor.
 *
 * Vertical: in cube and robot stretches the camera follows freely with a dead zone (regular
 * jumps do not move it) and keeps the floor in view; in vehicle stretches it frames the
 * corridor band of the level's sections. All changes are smoothed; snap() jumps (start,
 * respawn).
 *
 * HUD: framing and zoom leave out the area under the top bar (insetTop). Controls along the
 * bottom edge (the boost buttons) sit on the ground: free stretches keep the floor above
 * them at the same zoom; a corridor — a vehicle stretch's, or the one of a whole ball or wave
 * level from the start to the finish — keeps its floor above them too (its obstacles come
 * along its floor and ceiling), with its spare height and, where that is not enough (a tall
 * UFO or wave corridor on a small phone), zooming out as far as it takes.
 */
import { DT, type LevelData, type LevelSection, type Mode, SPEEDS } from '@cube-rush/shared';

/** Horizontal position of the followed racer, as a fraction of the view width. */
export const PLAYER_SCREEN_X = 0.3;
export const LOOKAHEAD_SECONDS = 1.2;
export const MIN_LOOKAHEAD_BLOCKS = 11;
/** Visible height (blocks) asked for in free-camera (cube) stretches. */
export const MIN_FREE_HEIGHT = 7;
/** Space kept around a framed corridor band, blocks. */
export const BAND_PAD = 0.6;
/** Ground shown below the floor in free stretches, as a fraction of the visible height. */
const GROUND_FRACTION = 0.18;
/**
 * Controls at the bottom of a view (boost buttons) cover the ground below the floor, not
 * the floor itself: free stretches show this much more ground than the controls cover
 * (blocks), but never more than MAX_GROUND_FRACTION of the visible height.
 */
const CONTROL_CLEARANCE = 0.15;
const MAX_GROUND_FRACTION = 0.45;
/** Dead zone of the free camera: the racer may move between these fractions of the height. */
const DEAD_ZONE_LOW = 0.25;
const DEAD_ZONE_HIGH = 0.6;
/** The racer is always kept at least this far (blocks) inside the top and bottom edges. */
const EDGE_MARGIN = 0.7;
const MIN_PPB = 6;
const MAX_PPB = 220;
/** Smoothing time constants, seconds. */
const ZOOM_TIME = 0.35;
const FREE_TIME = 0.16;
const FRAMED_TIME = 0.24;

/**
 * Modes whose corridor the camera frames (GD: everything but the cube and the robot, which jump
 * about freely: the camera follows them).
 */
export function isFramedMode(mode: Mode): boolean {
  return mode !== 'cube' && mode !== 'robot';
}

/** Blocks ahead of the racer the view must show at `speed` blocks/s. */
export function lookaheadBlocks(speed: number): number {
  return Math.max(MIN_LOOKAHEAD_BLOCKS, speed * LOOKAHEAD_SECONDS);
}

/** Zoom of a `width` × `height` px view that shows `lookahead` blocks ahead and `minHeight` blocks tall. */
export function pixelsPerBlock(
  width: number,
  height: number,
  lookahead: number,
  minHeight: number,
): number {
  const byWidth = (width * (1 - PLAYER_SCREEN_X)) / lookahead;
  const byHeight = height / minHeight;
  const ppb = Math.min(byWidth, byHeight);
  return Number.isFinite(ppb) ? Math.min(MAX_PPB, Math.max(MIN_PPB, ppb)) : MIN_PPB;
}

/** Vertical needs of the camera somewhere in a level. */
export interface ViewNeeds {
  /** Visible height in blocks. */
  height: number;
  /** Horizontal speed in blocks/s. */
  speed: number;
  /** The tallest corridor band (blocks, padding left out) of a framed section; 0 for none. */
  framed: number;
}

/** Visible height a section asks for. */
export function sectionHeight(section: LevelSection): number {
  return isFramedMode(section.mode) ? section.maxY - section.minY + 2 * BAND_PAD : MIN_FREE_HEIGHT;
}

/** Camera hints of one level: its sections and where the speed changes. */
export class LevelGuide {
  private readonly sections: LevelSection[];
  /** Speed changes: from x `speedX[i]` on, the speed is `speedValue[i]` blocks/s. */
  private readonly speedX: number[] = [];
  private readonly speedValue: number[] = [];
  /** The run's clock: from tick `clockTick[i]`, at x `clockX[i]`, `clockStep[i]` blocks a tick. */
  private readonly clockTick: number[] = [];
  private readonly clockX: number[] = [];
  private readonly clockStep: number[] = [];
  /**
   * A whole ball or wave level (it starts in a framed mode): the band of its one corridor;
   * null for a cube level.
   */
  readonly corridor: { readonly minY: number; readonly maxY: number } | null;

  constructor(level: Pick<LevelData, 'sections' | 'objects' | 'start'>) {
    this.sections = [...level.sections].sort((a, b) => a.fromX - b.fromX);
    this.corridor =
      isFramedMode(level.start.mode) && this.sections.length > 0
        ? {
            minY: Math.min(...this.sections.map((s) => s.minY)),
            maxY: Math.max(...this.sections.map((s) => s.maxY)),
          }
        : null;
    const changes: Array<[number, number]> = [];
    for (const o of level.objects) {
      if (o.type !== 'portal' || !o.portal.startsWith('speed-')) continue;
      const key = Number(o.portal.slice(6));
      const speed = SPEEDS[key as 0 | 1 | 2 | 3];
      if (speed !== undefined) changes.push([o.x + o.w / 2, speed]);
    }
    changes.sort((a, b) => a[0] - b[0]);
    this.speedX.push(Number.NEGATIVE_INFINITY);
    this.speedValue.push(SPEEDS[level.start.speed] ?? SPEEDS[1]);
    for (const [x, speed] of changes) {
      this.speedX.push(x);
      this.speedValue.push(speed);
    }
    // The run's clock (see tickAt): as the physics, a speed portal acts from the tick after the
    // one the centre crossed its middle in.
    let x = level.start.x;
    let tick = 0;
    let step = (SPEEDS[level.start.speed] ?? SPEEDS[1]) * DT;
    this.clockTick.push(tick);
    this.clockX.push(x);
    this.clockStep.push(step);
    for (const [at, speed] of changes) {
      if (at <= x) continue;
      const ticks = Math.ceil((at - x) / step - 1e-9);
      tick += ticks;
      x += ticks * step;
      step = speed * DT;
      this.clockTick.push(tick);
      this.clockX.push(x);
      this.clockStep.push(step);
    }
  }

  /**
   * The tick of an attempt (fractional) at which a racer's centre is at x — x depends on the
   * tick only (speed portals act on the whole column): moving saws, blocks and rotors are drawn
   * where the physics has them then (a view shows its racer's own moment of the level).
   */
  tickAt(x: number): number {
    let i = 0;
    while (i + 1 < this.clockX.length && this.clockX[i + 1]! <= x) i++;
    return this.clockTick[i]! + (x - this.clockX[i]!) / this.clockStep[i]!;
  }

  /** Where a racer's centre is at a tick of an attempt (fractional): tickAt, the other way. */
  xAt(tick: number): number {
    let i = 0;
    while (i + 1 < this.clockTick.length && this.clockTick[i + 1]! <= tick) i++;
    return this.clockX[i]! + (tick - this.clockTick[i]!) * this.clockStep[i]!;
  }

  /** The section covering x (the nearest one outside the covered range), or null if none. */
  sectionAt(x: number): LevelSection | null {
    const sections = this.sections;
    if (sections.length === 0) return null;
    let lo = 0;
    let hi = sections.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (sections[mid]!.fromX <= x) lo = mid;
      else hi = mid - 1;
    }
    return sections[lo]!;
  }

  /** The speed (blocks/s) at x: the level's start speed, or the last speed portal's before x. */
  speedAt(x: number): number {
    let speed = this.speedValue[0]!;
    for (let i = 1; i < this.speedX.length && this.speedX[i]! <= x; i++) {
      speed = this.speedValue[i]!;
    }
    return speed;
  }

  /** The strictest needs within [x1, x2]: tallest band, highest speed. Writes into `out`. */
  needs(x1: number, x2: number, out: ViewNeeds): ViewNeeds {
    let height = MIN_FREE_HEIGHT;
    let framed = 0;
    for (const section of this.sections) {
      if (section.toX < x1 || section.fromX > x2) continue;
      height = Math.max(height, sectionHeight(section));
      if (isFramedMode(section.mode)) framed = Math.max(framed, section.maxY - section.minY);
    }
    let speed = 0;
    for (let i = 0; i < this.speedX.length; i++) {
      const from = this.speedX[i]!;
      const to = this.speedX[i + 1] ?? Number.POSITIVE_INFINITY;
      if (to > x1 && from <= x2) speed = Math.max(speed, this.speedValue[i]!);
    }
    out.height = height;
    out.speed = speed;
    out.framed = framed;
    return out;
  }
}

/** What the camera follows. */
export interface CameraTarget {
  x: number;
  y: number;
  gravity: 1 | -1;
}

export class Camera {
  /** Pixels per block. */
  ppb = 40;
  /** World x at the left edge and world y at the bottom edge of the view. */
  left = 0;
  bottom = 0;
  /** View size in CSS pixels. */
  width = 1;
  height = 1;
  /**
   * CSS pixels at the top of the view covered by the HUD bar. Framing and zoom use the
   * area below it, so a ship corridor's ceiling never hides under the buttons.
   */
  insetTop = 0;
  /**
   * CSS pixels at the bottom of the view covered by HUD controls (the boost buttons on a
   * phone). Free stretches keep the floor above them — the spikes coming up on the floor
   * stay visible — and corridors use spare height for that; the zoom never changes for it.
   */
  insetBottom = 0;
  /** Dead-zone target of the vertical follow (before smoothing). */
  private targetBottom = 0;
  private shakeLeft = 0;
  private shakeStrength = 0;
  private readonly needsScratch: ViewNeeds = { height: MIN_FREE_HEIGHT, speed: 0, framed: 0 };

  get visibleWidth(): number {
    return this.width / this.ppb;
  }

  get visibleHeight(): number {
    return this.height / this.ppb;
  }

  /** Visible height below the HUD inset, blocks: what framing and the dead zone work with. */
  get usableHeight(): number {
    return Math.max(1, this.height - this.insetTop) / this.ppb;
  }

  /** Current shake offset in blocks (0 when calm). */
  shakeOffset(time: number, axis: 0 | 1): number {
    if (this.shakeLeft <= 0) return 0;
    const fade = this.shakeLeft * this.shakeLeft;
    return Math.sin(time * (axis === 0 ? 71 : 53) + axis) * this.shakeStrength * fade;
  }

  /** A short shake (death); strength in blocks. */
  shake(strength: number): void {
    this.shakeStrength = strength;
    this.shakeLeft = 1;
  }

  /** Jumps straight to the target: level start, respawn, a new view. */
  snap(target: CameraTarget, guide: LevelGuide, width: number, height: number): void {
    this.resize(width, height);
    this.ppb = this.zoomFor(target, guide, true);
    this.left = target.x - PLAYER_SCREEN_X * this.visibleWidth;
    const section = guide.sectionAt(target.x);
    this.targetBottom = this.restingBottom(target, section);
    this.targetBottom = this.verticalTarget(target, section);
    this.bottom = this.keepInside(target, this.targetBottom);
  }

  /** Follows the target smoothly; `dt` in real seconds. */
  follow(target: CameraTarget, guide: LevelGuide, width: number, height: number, dt: number): void {
    this.resize(width, height);
    const zoom = this.zoomFor(target, guide, false);
    // Smooth in log space: zooming in and out feel the same.
    this.ppb *= (zoom / this.ppb) ** (1 - Math.exp(-dt / ZOOM_TIME));
    this.left = target.x - PLAYER_SCREEN_X * this.visibleWidth;
    const section = guide.sectionAt(target.x);
    this.targetBottom = this.verticalTarget(target, section);
    const time = section && isFramedMode(section.mode) ? FRAMED_TIME : FREE_TIME;
    const next = this.bottom + (this.targetBottom - this.bottom) * (1 - Math.exp(-dt / time));
    this.bottom = this.keepInside(target, next);
    if (this.shakeLeft > 0) this.shakeLeft = Math.max(0, this.shakeLeft - dt / 0.35);
  }

  /** Follows only the zoom and size (the racer is dead: GD keeps the camera still). */
  hold(width: number, height: number, dt: number): void {
    const visibleBefore = this.visibleWidth;
    this.resize(width, height);
    if (visibleBefore !== this.visibleWidth) {
      // Keep the racer's screen position when the view changes size.
      this.left += (visibleBefore - this.visibleWidth) * PLAYER_SCREEN_X;
    }
    if (this.shakeLeft > 0) this.shakeLeft = Math.max(0, this.shakeLeft - dt / 0.35);
  }

  private resize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
  }

  private zoomFor(target: CameraTarget, guide: LevelGuide, snapping: boolean): number {
    // Look at what is coming within the current view (and a bit beyond) so the zoom changes
    // before the racer gets there.
    const ahead = snapping ? MIN_LOOKAHEAD_BLOCKS * 2 : this.visibleWidth * 1.15;
    const needs = guide.needs(target.x - 2, target.x + ahead, this.needsScratch);
    const speed = needs.speed > 0 ? needs.speed : SPEEDS[1];
    const usable = Math.max(1, this.height - this.insetTop);
    const ppb = pixelsPerBlock(this.width, usable, lookaheadBlocks(speed), needs.height);
    const corridor = guide.corridor;
    const framed = corridor ? corridor.maxY - corridor.minY : needs.framed;
    if (framed <= 0 || this.insetBottom <= 0) return ppb;
    // A corridor (a whole ball or wave level's, or a vehicle stretch coming up): its floor stays
    // above the controls (with some clearance), the band's padding at the bottom giving way to
    // them: verticalTarget then puts the spare height below the band.
    const band = framed + BAND_PAD + CONTROL_CLEARANCE;
    const lifted = Math.max(1, usable - this.insetBottom) / band;
    return Math.max(MIN_PPB, Math.min(ppb, lifted));
  }

  /** Blocks at the bottom of the view the controls cover, with some clearance (0: none). */
  private get controlBlocks(): number {
    return this.insetBottom > 0 ? this.insetBottom / this.ppb + CONTROL_CLEARANCE : 0;
  }

  /** Where the free camera rests with the racer on the floor of a section. */
  private restingBottom(target: CameraTarget, section: LevelSection | null): number {
    const visible = this.usableHeight;
    const ground = Math.max(BAND_PAD, visible * GROUND_FRACTION);
    if (!section) return target.y - visible * 0.3;
    if (target.gravity < 0) return section.maxY + ground - visible;
    // The floor stays clear of the controls at the bottom (a small view only does what it can).
    const clear = Math.max(ground, Math.min(this.controlBlocks, visible * MAX_GROUND_FRACTION));
    return section.minY - clear;
  }

  private verticalTarget(target: CameraTarget, section: LevelSection | null): number {
    const visible = this.usableHeight;
    if (!section) return target.y - visible / 2;
    const { minY, maxY } = section;
    if (isFramedMode(section.mode)) {
      const band = maxY - minY + 2 * BAND_PAD;
      if (band <= visible) {
        // Centred; spare height goes below the band instead where controls cover the bottom.
        const spare = visible - band;
        const below = Math.max(spare / 2, Math.min(spare, this.controlBlocks));
        return minY - BAND_PAD - below;
      }
      return clamp(target.y - visible / 2, minY - BAND_PAD, maxY + BAND_PAD - visible);
    }
    // Free: dead zone around the racer...
    const low = target.gravity > 0 ? DEAD_ZONE_LOW : 1 - DEAD_ZONE_HIGH;
    const high = target.gravity > 0 ? DEAD_ZONE_HIGH : 1 - DEAD_ZONE_LOW;
    let bottom = this.targetBottom;
    if (target.y < bottom + low * visible) bottom = target.y - low * visible;
    if (target.y > bottom + high * visible) bottom = target.y - high * visible;
    // ...within the views that show the floor (with some ground) or the band's far side.
    const floor = this.restingBottom(target, section);
    const far = target.gravity > 0 ? maxY + BAND_PAD - visible : minY - BAND_PAD;
    return clamp(bottom, Math.min(floor, far), Math.max(floor, far));
  }

  /** Never lets the racer leave the view vertically. */
  private keepInside(target: CameraTarget, bottom: number): number {
    const visible = this.usableHeight;
    const margin = Math.min(EDGE_MARGIN, visible / 2);
    return clamp(bottom, target.y + margin - visible, target.y - margin);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
